import { Button } from '@wot/components';
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { plansApi } from '../../../api/plan';
import { LOADING } from '../../../assets/animated';
import { ARROWDOWN_ICON, MORE_SVG } from '../../../assets/icons';
import { CAUTION, UNDER_REVIEW } from '../../../assets/images';
import { Portal } from '../../../components/Portal';
import { useAppSelector, useClickOutside } from '../../../hooks';
import { LPlan } from '../../../interfaces/Plan';
import xhr from '../../../utils/fetch';
import TutorCard from '../../../components/TutorCard';
import { PlanCard } from '../../MyPlans';

export default function TransferRequests() {
  const [transferRequests, setTransferRequests] = useState<LPlan[]>([]);
  const { userData } = useAppSelector((state) => state.userSlice);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userData?._id) {
      setLoading(true);
      plansApi
        .get(userData?._id)
        .then((data) => {
          setLoading(false);
          setTransferRequests(data.filter((plan: any) => 'changeRequest' in plan));
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, []);

  return (
    <div className="py-5 flex flex-col gap-5">
      {loading ? (
        <div className="w-full min-h-[256px] flex items-center justify-center">
          <img src={LOADING} className="w-10 h-10" alt="loading" />
        </div>
      ) : (
        <>
          {transferRequests.length > 0 ? (
            transferRequests.map((req, i) => (
              <TransferRequestItem key={i} plan={req} timezone={userData!.preference?.timezone} />
            ))
          ) : (
            <>
              <div className="flex flex-col gap-5 items-center py-10">
                <img src={CAUTION} alt="No transfer requests" className="rounded-full" />
                <div className="flex flex-col items-center w-full gap-2">
                  <span className="text-2xl font-medium">Yay!</span>
                  <span className="lg:w-2/5 w-3/5 text-base font-normal text-center">
                    You don’t have any Transfer Requests.
                  </span>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export function TransferRequestItem({ plan, timezone }: { plan: LPlan; timezone?: string }) {
  const [expand, setExpand] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [suggestedTutors, setSuggestedTutors] = useState([]);
  const navigate = useNavigate();
  const [viewMoreOpt, setViewMoreOpt] = useState(false);
  const viewMoreRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useClickOutside(viewMoreRef, () => setViewMoreOpt(false));

  const deleteRequest = () => {
    plansApi
      .deleteChangeTutorRequest(plan._id)
      .then(() => {
        toast.success('Change Request Deleted successfully');
      })
      .catch(() => {
        toast.error('Something went wrong!');
      });
  };

  function ViewMore() {
    return (
      <div
        ref={viewMoreRef}
        className="flex flex-col absolute z-50 top-6 right-0 bg-white drop-shadow-lg py-2 px-1 rounded-lg cursor-pointer"
      >
        <span
          className="whitespace-nowrap py-2 px-1 rounded-md text-sm hover:bg-neutralBlue2"
          onClick={() => {
            setShowConfirmation(true);
          }}
        >
          Delete Request
        </span>
      </div>
    );
  }

  useEffect(() => {
    xhr({
      url: `/u/${plan.userId}/plans/${plan._id}/suggested-tutors`,
      method: "GET"
    }).then((res) => {
      if (res.data && res.data.length > 0) {
        setSuggestedTutors(res.data[0].suggestedTutors.filter((item: any) => item._id !== plan.tutorId));
      }
    }).catch(err => console.log(err));
  }, []);

  const assignNewTutor = (id: string) => {
    setLoading(true)
    xhr({
      url: `/u/${plan.userId}/plans/${plan._id}/assign-tutor`,
      method: "PUT",
      body: {
        tutorId: id
      }
    }).then((res) => {
      if (res.data) {
        toast.success('Successfully assigned a new tutor.');
        navigate("/my-plans");
      }
      setLoading(false)
    }).catch(err => {
      toast.error('Something went wrong.');
      setLoading(false)
    });
  }


  return (
    <div className="w-full flex flex-col gap-2 p-7 bg-white rounded-2xl lg:border-2 lg:border-neutralGray3 drop-shadow-md">
      <div className="flex flex-col gap-5">
        <div className="flex">
          <div className="w-full flex flex-col">
            <h1 className="text-lg font-medium">Change Tutor Request</h1>
          </div>
          <div className="flex h-max items-start justify-end gap-7 relative">
            <p className="text-sm md:text-base font-normal whitespace-nowrap">
              {formatInTimeZone(plan.createdAt, timezone || 'IST', 'd LLL y')}
            </p>
            {plan.changeRequest?.status === "pending" &&
              <img src={MORE_SVG} alt="more icon" className="h-5 cursor-pointer" onClick={() => setViewMoreOpt(true)} />
            }
            {viewMoreOpt && <ViewMore />}
          </div>
        </div>
        <PlanCard plan={plan} />
      </div>
      {expand && !plan.changeRequest?.fulfilled && (
        <div className="w-full flex flex-col gap-4 pt-5">
          <div className="border-t-2 border-neutralGray3"></div>
          {plan.changeRequest?.status === 'approved' ? (
            <>
              <div>
                <h2 className="text-2xl font-normal">Suggested Tutors</h2>
              </div>
              {loading ?
                <div className='w-full h-full min-h-[256px] flex flex-col items-center justify-center'>
                  <img src={LOADING} className='w-14 h-14' />
                  <p className='text-base text-center font-medium'>Assigning new tutor....</p>
                </div>
                :
                <>
                  {suggestedTutors && suggestedTutors.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 lg:my-2 gap-4">
                        {suggestedTutors?.map((item: any, i) => (
                          <div className="w-full flex flex-col max-w-sm" key={i}>
                            <TutorCard item={item} />
                            <Button
                              block
                              type='primary'
                              text='Assign New tutor'
                              onClick={() => assignNewTutor(item._id)}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col gap-5 items-center py-10">
                      <img src={CAUTION} alt="No tutors found" />
                      <div className="flex flex-col items-center w-full gap-2">
                        <span className="text-2xl font-medium">Oops,</span>
                        <span className="w-11/12 md:w-2/5 text-sm font-normal text-center">No tutors found</span>
                      </div>
                      <Link to={'/contact'}>
                        <div className="max-w-[200px]">
                          <Button text="Contact Support" onClick={() => { }} size="lg" />
                        </div>
                      </Link>
                    </div>
                  )}
                </>
              }
            </>
          ) : plan.changeRequest?.status === 'pending' ? (
            <div className="flex flex-col gap-5 items-center py-10">
              <img src={UNDER_REVIEW} alt="Request under review" />
              <div className="flex flex-col items-center w-full gap-2">
                <span className="text-2xl font-medium">Under Review</span>
                <span className="w-1/2 font-normal text-center">
                  Your request for tutor change is still under review, please come back later. If you still need help
                  you may contact our support team.
                </span>
              </div>
              <Link to={'/contact'}>
                <div className="max-w-[200px]">
                  <Button text="Contact Support" onClick={() => { }} size="lg" />
                </div>
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-5 items-center py-10">
              <img src={CAUTION} alt="No tutors found" />
              <div className="flex flex-col items-center w-full gap-2">
                <span className="text-2xl font-medium">Oops,</span>
                <span className="w-11/12 md:w-2/5 text-sm font-normal text-center">
                  Your request for tutor change has been rejected by the admin. If you still need help you may contact
                  our support team.
                </span>
              </div>
              <Link to={'/contact'}>
                <div className="max-w-[200px]">
                  <Button text="Contact Support" onClick={() => { }} size="lg" />
                </div>
              </Link>
            </div>
          )}
        </div>
      )}
      {!plan.changeRequest?.fulfilled &&
        <div className="w-full pt-4 flex items-center justify-center animate-bounce">
          <img
            src={ARROWDOWN_ICON}
            alt="Expand details"
            className={`w-4 h-4 cursor-pointer ${expand ? 'rotate-180' : ''}`}
            onClick={() => setExpand(!expand)}
          />
        </div>
      }
      {showConfirmation && (
        <Portal>
          <div className="fixed w-full h-screen top-0 left-0 z-50">
            <div className="w-full h-full relative flex items-center justify-center">
              <div
                onClick={() => setShowConfirmation(false)}
                className="absolute top-0 left-0 bg-black/75 w-full h-full z-10"
              ></div>

              <div className="w-11/12 max-w-[420px] h-max flex flex-col gap-7 p-7 bg-white rounded-xl z-20 overflow-hidden">
                <div className="flex">
                  <div className="w-full relative px-4 flex items-center justify-center ">
                    <h3 className="text-xl font-semibold text-black text-center">Confirm Delete</h3>
                  </div>
                  <MdClose onClick={() => setShowConfirmation(false)} className="text-xl cursor-pointer ml-auto" />
                </div>

                <p className="w-full text-sm text-gray-400 text-center">
                  Are You Sure You Want To Delete this Tutor change request?
                </p>
                <div className="w-full flex items-center justify-center gap-4">
                  <Button text="Cancel" type="primary" onClick={() => setShowConfirmation(false)} />
                  <Button
                    text="Yes, Delete"
                    type="secondary"
                    onClick={() => {
                      setShowConfirmation(false);
                      deleteRequest();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
